import { httpClient } from '@bs/core'

export const POS_TYPES = {
  COVA: 'cova',
  GREENLINE: 'greenline',
} as const

export type PosType = (typeof POS_TYPES)[keyof typeof POS_TYPES]

export interface LinkPosPayload {
  posType: PosType
  // cova fields
  covaCompanyId?: string
  covaUsername?: string
  covaPassword?: string
  covaClientId?: string
  covaClientSecret?: string
  // greenline fields
  greenlineCompanyId?: string
  // greenline-only: switches tax-sync logic to Tax V2; never sent for other pos types
  posGreenlineUsingTaxV2?: boolean
}

export const linkPOS = (orgId: string, payload: LinkPosPayload) =>
  httpClient.json.post(`/organizations/${orgId}/link-pos`, {
    pos_type: payload.posType,
    ...(payload.covaCompanyId
      ? { cova_company_id: payload.covaCompanyId }
      : {}),
    ...(payload.covaUsername ? { cova_username: payload.covaUsername } : {}),
    ...(payload.covaPassword ? { cova_password: payload.covaPassword } : {}),
    ...(payload.covaClientId ? { cova_client_id: payload.covaClientId } : {}),
    ...(payload.covaClientSecret
      ? { cova_client_secret: payload.covaClientSecret }
      : {}),
    ...(payload.greenlineCompanyId
      ? { greenline_company_id: payload.greenlineCompanyId }
      : {}),
    ...(payload.posType === POS_TYPES.GREENLINE &&
    payload.posGreenlineUsingTaxV2 !== undefined
      ? { pos_greenline_using_tax_v2: payload.posGreenlineUsingTaxV2 }
      : {}),
  })

export const enableMockPOS = (orgId: string) =>
  httpClient.json.post(`/organizations/${orgId}/mock-pos`, {})

export const removeMockPOS = (orgId: string) =>
  httpClient.json.delete(`/organizations/${orgId}/mock-pos`)

export const getPOSLocations = (orgId: string) =>
  httpClient.json.get(`/organizations/${orgId}/pos-locations`)

export interface PosLocation {
  id: string
  name: string
  // Greenline only — multi-company stores return rows across every company.
  company_id?: number | string
  // Cova rows split the street address; Greenline rows use a single `address`.
  address_1?: string
  address_2?: string
  address?: string
  city?: string
  province?: string
  postal_code?: string
  phone_number?: string | null
}

export const normalizePosLocations = (res: unknown): PosLocation[] => {
  const list = Array.isArray(res)
    ? res
    : Array.isArray((res as { items?: unknown[] })?.items)
      ? (res as { items: unknown[] }).items
      : Array.isArray((res as { data?: unknown[] })?.data)
        ? (res as { data: unknown[] }).data
        : []
  return (list as PosLocation[])
    .filter((loc) => loc && loc.id != null && String(loc.id) !== '')
    .map((loc) => ({ ...loc, id: String(loc.id) }))
}

// Greenline multi-company: attach another Greenline company to a pantry store.
export const addGreenlineCompany = (
  storeId: string,
  greenlineCompanyId: number,
) =>
  httpClient.json.post(`/pantry/stores/${storeId}/add-company`, {
    greenline_company_id: greenlineCompanyId,
  })

export interface LinkWarehousePosOptions {
  companyId?: number | string
  oneStoreManyCompanies?: boolean
}

export const linkPantryWarehouseToPOS = (
  orgId: string,
  warehouseId: string,
  posId: string,
  opts?: LinkWarehousePosOptions,
) =>
  httpClient.json.post(
    `/organizations/${orgId}/pantry-warehouses/${warehouseId}/link-pos`,
    {
      pos_id: posId,
      ...(opts?.companyId != null && opts.companyId !== ''
        ? { company_id: Number(opts.companyId) }
        : {}),
      ...(opts?.oneStoreManyCompanies !== undefined
        ? { one_store_manny_companies: opts.oneStoreManyCompanies }
        : {}),
    },
  )
